import React, { useMemo, useCallback, useContext, useRef, useState } from 'react';
import { OrderContext } from 'ggApp/shared/context/Order/OrderContext';

import { DropDownWrapper, DropDown, ItemText, ColorBox } from './styles';

interface VariantProps {
    item: {
        id: number;
        variant: {
            id: number;
            colorTitle: string;
            colorCodes: Array<string>;
        };
        itemId?: string | number;
        variants?: Array<{
            id: number;
            color: {
                title: string;
                name: string;
                codes: Array<string>;
            };
        }>;
    };
    isOrderReview?: boolean;
}

export const Variant = ({ item, isOrderReview }: VariantProps): JSX.Element | null => {
    const ddRef = useRef(null);
    const [offsetTop, setOffsetTop] = useState(ddRef.current?.offsetTop ?? 0);

    const { send, activeStore, customizeColorFlag } = useContext(OrderContext);

    const overlayId = document.getElementById('lineItemsWrap');

    const handler = useCallback(() => {
        overlayId?.click(); // this is an ugly hack to close the dropdown on scroll, sorry
        overlayId?.removeEventListener('scroll', handler);
    }, [overlayId]);

    const handleOpen = useCallback(() => {
        overlayId.addEventListener('scroll', handler);

        const offset =
            ddRef.current.getBoundingClientRect().top -
            ddRef.current.offsetParent.getBoundingClientRect().top;

        setOffsetTop(offset);
    }, [overlayId, handler, ddRef]);

    const option = useMemo(() => {
        const variant = item?.variant;

        const { colorTitle, colorCodes, id } = variant || {};

        if (!colorTitle) {
            return null;
        }

        const code = (Array.isArray(colorCodes) && colorCodes[0]) || colorTitle;

        return {
            value: id,
            label: colorTitle,
            icon: <ColorBox color={code} />,
        };
    }, [item]);

    const options = useMemo(() => {
        if (!customizeColorFlag || !item?.variants) {
            return [option];
        }

        return item?.variants.map((i: any) => ({
            value: i.id,
            label: i.color.title?.[activeStore?.activeLanguage ?? 'de'] || i.color.name,
            icon: <ColorBox color={i.color?.codes?.[0]} />,
        }));
    }, [customizeColorFlag, item, option, activeStore]);

    const onChange = useCallback(
        (i) => {
            send('UPDATE_LINE_ITEM', {
                item: {
                    id: item.id,
                    variant_id: i.value,
                },
            });
        },
        [item, send],
    );

    if (!option) {
        return null;
    }

    return (
        <DropDownWrapper
            ref={ddRef}
            isOrderReview={isOrderReview}
            $top={offsetTop}
            $left={ddRef.current?.offsetLeft}
            $width={ddRef.current?.offsetWidth}
        >
            <DropDown
                overrides={{
                    ItemText: { component: ItemText },
                }}
                defaultValue={option}
                options={options}
                onChange={onChange}
                onOpen={handleOpen}
            />
        </DropDownWrapper>
    );
};
